import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import Logo from "./Logo";
import { SERVICE_PAGES } from "../lib/api";

const serviceList = Object.values(SERVICE_PAGES);

function NavItem({ to, children, testid, dark }) {
  return (
    <NavLink
      to={to}
      data-testid={testid}
      className={({ isActive }) =>
        `text-[14px] font-semibold tracking-tight transition-colors duration-200 ${
          isActive
            ? dark ? "text-white" : "text-navy"
            : dark ? "text-ice/70 hover:text-white" : "text-slate hover:text-navy"
        }`
      }
    >
      {children}
    </NavLink>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const location = useLocation();
  const dark = location.pathname === "/" && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setDropOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-md border-b border-line shadow-[0_1px_0_rgba(16,31,56,0.02)]"
          : dark
          ? "bg-navy-950/40 backdrop-blur-md border-b border-white/10"
          : "bg-white/60 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-[72px]">
          <Link to="/" data-testid="nav-home-link" aria-label="Bottom Line Improvements — home">
            <Logo dark={dark} />
          </Link>

          <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
            <div
              className="relative"
              onMouseEnter={() => setDropOpen(true)}
              onMouseLeave={() => setDropOpen(false)}
            >
              <button
                data-testid="nav-services-dropdown"
                aria-expanded={dropOpen}
                aria-haspopup="true"
                onClick={() => setDropOpen((v) => !v)}
                className={`flex items-center gap-1.5 text-[14px] font-semibold tracking-tight transition-colors ${
                  dark ? "text-ice/80 hover:text-white" : "text-slate hover:text-navy"
                }`}
              >
                Savings Services
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${dropOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {dropOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 6, scale: 0.98 }}
                    transition={{ duration: 0.18 }}
                    className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[340px]"
                  >
                    <div className="bg-white border border-line rounded-2xl shadow-lift p-2">
                      {serviceList.map((s) => (
                        <Link
                          key={s.key}
                          to={s.path}
                          data-testid={`nav-service-${s.key}`}
                          className="group flex items-start justify-between gap-3 rounded-xl px-4 py-3 hover:bg-ice transition-colors"
                        >
                          <span>
                            <span className="block text-[14px] font-bold text-navy group-hover:text-royal">
                              {s.short}
                            </span>
                            <span className="block text-xs text-slate mt-0.5">
                              {s.key === "expense" && "Operating costs, shipping, payment processing"}
                              {s.key === "technology" && "Connectivity, cloud, software & collaboration"}
                              {s.key === "benefits" && "Healthcare coverage, virtual care & benefits"}
                              {s.key === "tax" && "Credits, incentives & property opportunities"}
                            </span>
                          </span>
                          <ArrowUpRight className="w-4 h-4 text-teal mt-1 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <NavItem to="/about" testid="nav-about-link" dark={dark}>About Us</NavItem>
            <NavItem to="/contact" testid="nav-contact-link" dark={dark}>Contact</NavItem>
            <Link
              to="/contact"
              data-testid="nav-start-assessment-button"
              className={`group relative inline-flex items-center gap-2 text-[14px] font-bold px-5 py-2.5 rounded-full overflow-hidden transition-transform duration-200 hover:-translate-y-0.5 ${
                dark ? "bg-teal text-navy" : "bg-navy text-white"
              }`}
            >
              <span className={`absolute inset-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out ${dark ? "bg-teal-bright" : "bg-teal"}`} />
              <span className="relative group-hover:text-navy transition-colors">Start Savings Review</span>
              <ArrowUpRight className="relative w-4 h-4 group-hover:text-navy transition-colors" />
            </Link>
          </nav>

          <button
            data-testid="nav-mobile-toggle"
            className={`lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border transition-colors ${
              dark ? "border-white/20 bg-white/10 text-white" : "border-line bg-white text-navy"
            }`}
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.22 }}
            className="lg:hidden fixed inset-0 top-16 bg-offwhite z-40 overflow-y-auto"
            data-testid="nav-mobile-menu"
          >
            <div className="px-5 py-6 flex flex-col gap-1">
              <p className="text-[11px] font-bold uppercase tracking-wider2 text-slate-light px-3 pb-2">Savings Services</p>
              {serviceList.map((s) => (
                <Link
                  key={s.key}
                  to={s.path}
                  data-testid={`nav-mobile-service-${s.key}`}
                  className="flex items-center justify-between rounded-xl bg-white border border-line px-4 py-3.5 text-[15px] font-bold text-navy"
                >
                  {s.short}
                  <ArrowUpRight className="w-4 h-4 text-teal" />
                </Link>
              ))}
              <div className="h-px bg-line my-4" />
              <Link to="/about" data-testid="nav-mobile-about" className="px-3 py-3 text-[17px] font-bold text-navy">About Us</Link>
              <Link to="/contact" data-testid="nav-mobile-contact" className="px-3 py-3 text-[17px] font-bold text-navy">Contact</Link>
              <Link
                to="/contact"
                data-testid="nav-mobile-start-assessment"
                className="mt-4 inline-flex justify-center items-center gap-2 bg-navy text-white font-bold px-6 py-4 rounded-full"
              >
                Start Savings Review
                <ArrowUpRight className="w-4 h-4" />
              </Link>
              <p className="text-center text-xs text-slate mt-6">A division of Community Promotions · Helping businesses grow since 2001</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
